import type { FormEvent, ReactElement } from 'react';
import { useState } from 'react';
import type { AdminCities, CityPublic } from '@cookspace-shared-contracts/core';
import { GitMerge, Pencil, Plus, Trash2 } from 'lucide-react';

import {
    useCreateCityMutation,
    useDeleteCityMutation,
    useListCitiesQuery,
    useMergeCityMutation,
    useReorderCitiesMutation,
    useUpdateCityMutation
} from '@/api/citiesApi';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import { SortableList } from '@/components/SortableList';
import { Switch } from '@/components/Switch';
import { getCityMergeErrorMessage } from '@/lib/cityMergeErrors';

const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

interface FormState {
    name: string;
    slug: string;
    fiasId: string;
    regionName: string;
    sortOrder: string;
    isActive: boolean;
}

interface FormErrors {
    name?: string;
    slug?: string;
}

const EMPTY_FORM: FormState = {
    name: '',
    slug: '',
    fiasId: '',
    regionName: '',
    sortOrder: '0',
    isActive: true
};

/** 409 от backend = конфликт уникальности (slug/fiasId) или город используется. */
function isConflict(error: unknown): boolean {
    return (
        typeof error === 'object' &&
        error !== null &&
        'status' in error &&
        (error as { status?: unknown }).status === 409
    );
}

/** Пустое поле формы → null (опциональные nullable-поля контракта). */
function toNullable(value: string): string | null {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
}

export function CitiesPage(): ReactElement {
    const [filterActiveOnly, setFilterActiveOnly] = useState(false);

    const { data, isLoading, isError } = useListCitiesQuery(
        filterActiveOnly ? { isActive: true } : {}
    );

    const [createCity, { isLoading: isCreating }] = useCreateCityMutation();
    const [updateCity, { isLoading: isUpdating }] = useUpdateCityMutation();
    const [deleteCity] = useDeleteCityMutation();
    const [reorderCities] = useReorderCitiesMutation();
    const [mergeCity, { isLoading: isMerging }] = useMergeCityMutation();

    const [isModalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<CityPublic | null>(null);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState<FormErrors>({});
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [pageError, setPageError] = useState<string | null>(null);
    const [pendingDelete, setPendingDelete] = useState<CityPublic | null>(null);

    // Слияние: источник (город-дубль) + выбранная цель + ошибка модалки.
    const [mergeSource, setMergeSource] = useState<CityPublic | null>(null);
    const [mergeTargetId, setMergeTargetId] = useState('');
    const [mergeError, setMergeError] = useState<string | null>(null);

    const items = data?.items ?? [];

    // Цели слияния — только активные города, кроме самого источника
    // (источник станет 301-маркером и деактивируется).
    const mergeTargets = mergeSource
        ? items.filter((c) => c.isActive && c.id !== mergeSource.id)
        : [];
    const mergeTarget = mergeTargets.find((c) => c.id === mergeTargetId) ?? null;

    const openCreate = (): void => {
        setEditing(null);
        setForm({ ...EMPTY_FORM, sortOrder: String(items.length) });
        setFormErrors({});
        setSubmitError(null);
        setModalOpen(true);
    };

    const openEdit = (city: CityPublic): void => {
        setEditing(city);
        setForm({
            name: city.name,
            slug: city.slug,
            fiasId: city.fiasId ?? '',
            regionName: city.regionName ?? '',
            sortOrder: String(city.sortOrder),
            isActive: city.isActive
        });
        setFormErrors({});
        setSubmitError(null);
        setModalOpen(true);
    };

    const validate = (): boolean => {
        const errors: FormErrors = {};
        if (!form.name.trim()) {
            errors.name = 'Название обязательно';
        }
        if (!form.slug.trim()) {
            errors.slug = 'Slug обязателен';
        } else if (!SLUG_REGEX.test(form.slug)) {
            errors.slug = 'Только латиница, цифры и дефисы';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        setSubmitError(null);
        if (!validate()) {
            return;
        }

        const sortOrderNum = Number(form.sortOrder);
        const sortOrder = Number.isFinite(sortOrderNum) && sortOrderNum >= 0 ? sortOrderNum : 0;

        try {
            if (editing) {
                const body: AdminCities.UpdateCityRequest = {
                    name: form.name.trim(),
                    slug: form.slug.trim(),
                    fiasId: toNullable(form.fiasId),
                    regionName: toNullable(form.regionName),
                    sortOrder,
                    isActive: form.isActive
                };
                await updateCity({ id: editing.id, body }).unwrap();
            } else {
                const body: AdminCities.CreateCityRequest = {
                    name: form.name.trim(),
                    slug: form.slug.trim(),
                    fiasId: toNullable(form.fiasId),
                    regionName: toNullable(form.regionName),
                    sortOrder,
                    isActive: form.isActive
                };
                await createCity(body).unwrap();
            }
            setModalOpen(false);
        } catch (error) {
            setSubmitError(
                isConflict(error)
                    ? 'Город с таким slug или ФИАС уже существует'
                    : 'Не удалось сохранить город'
            );
        }
    };

    const confirmDelete = async (): Promise<void> => {
        if (!pendingDelete) {
            return;
        }
        try {
            await deleteCity({ id: pendingDelete.id }).unwrap();
            setPendingDelete(null);
        } catch (error) {
            setPendingDelete(null);
            setPageError(
                isConflict(error)
                    ? 'Город используется — удаление невозможно, деактивируйте его'
                    : 'Не удалось удалить город'
            );
        }
    };

    const handleToggleActive = async (city: CityPublic, next: boolean): Promise<void> => {
        try {
            await updateCity({ id: city.id, body: { isActive: next } }).unwrap();
        } catch {
            setPageError('Не удалось изменить статус');
        }
    };

    const handleReorder = async (orderedIds: string[]): Promise<void> => {
        try {
            await reorderCities({ orderedIds }).unwrap();
        } catch {
            setPageError('Не удалось изменить порядок');
        }
    };

    const openMerge = (city: CityPublic): void => {
        setMergeSource(city);
        setMergeTargetId('');
        setMergeError(null);
    };

    const closeMerge = (): void => {
        setMergeSource(null);
        setMergeTargetId('');
        setMergeError(null);
    };

    const confirmMerge = async (): Promise<void> => {
        if (!mergeSource || mergeTargetId === '') {
            return;
        }
        setMergeError(null);
        try {
            await mergeCity({
                id: mergeSource.id,
                body: { targetCityId: mergeTargetId }
            }).unwrap();
            closeMerge();
        } catch (error) {
            setMergeError(getCityMergeErrorMessage(error, 'Не удалось объединить города'));
        }
    };

    return (
        <div data-testid="cities-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Города</h1>
                <button
                    type="button"
                    onClick={openCreate}
                    data-testid="city-create-button"
                    className="inline-flex items-center gap-2 rounded bg-gray-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
                >
                    <Plus size={14} />
                    Создать город
                </button>
            </div>

            <div className="flex flex-wrap items-center gap-4 rounded border border-gray-200 bg-white p-3">
                <Switch
                    checked={filterActiveOnly}
                    onChange={setFilterActiveOnly}
                    label="Только активные"
                    testId="city-filter-active"
                />
            </div>

            {pageError !== null && pageError !== '' && (
                <div
                    className="flex items-center justify-between rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                    data-testid="cities-page-error"
                >
                    <span>{pageError}</span>
                    <button
                        type="button"
                        onClick={() => setPageError(null)}
                        className="text-xs text-red-700 underline"
                    >
                        Закрыть
                    </button>
                </div>
            )}

            {isLoading && <div className="text-sm text-gray-500">Загрузка...</div>}
            {isError && (
                <div className="text-sm text-red-600" data-testid="cities-error">
                    Не удалось загрузить города
                </div>
            )}

            {!isLoading && !isError && (
                <div
                    className="overflow-hidden rounded border border-gray-200 bg-white"
                    data-testid="cities-table"
                >
                    <div className="grid grid-cols-[40px_80px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_80px_80px_180px] gap-2 border-b border-gray-200 bg-gray-50 px-2 py-2 text-xs font-medium uppercase text-gray-600">
                        <span />
                        <span>ID</span>
                        <span>Название</span>
                        <span>Slug</span>
                        <span>Регион</span>
                        <span>Порядок</span>
                        <span>Активен</span>
                        <span className="text-right">Действия</span>
                    </div>

                    {items.length === 0 && (
                        <div className="px-3 py-6 text-center text-sm text-gray-500">
                            Города не найдены
                        </div>
                    )}

                    <SortableList
                        items={items}
                        onReorder={handleReorder}
                        testId="cities-sortable"
                        renderItem={(city) => (
                            <div
                                className="grid grid-cols-[80px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_80px_80px_180px] items-center gap-2 border-b border-gray-100 px-2 py-2 text-sm last:border-b-0"
                                data-testid={`city-row-${city.id}`}
                            >
                                <span className="truncate font-mono text-xs text-gray-500">
                                    {city.id.slice(0, 8)}
                                </span>
                                <span className="truncate text-gray-900">{city.name}</span>
                                <span className="truncate text-gray-600">{city.slug}</span>
                                <span className="truncate text-gray-600">
                                    {city.regionName ?? '—'}
                                </span>
                                <span className="text-gray-700">{city.sortOrder}</span>
                                <span>
                                    <Switch
                                        checked={city.isActive}
                                        onChange={(next) => handleToggleActive(city, next)}
                                        testId={`city-active-${city.id}`}
                                    />
                                </span>
                                <span className="flex justify-end gap-1">
                                    <button
                                        type="button"
                                        onClick={() => openEdit(city)}
                                        data-testid={`city-edit-${city.id}`}
                                        className="rounded p-1.5 text-gray-600 transition hover:bg-gray-100"
                                        aria-label="Редактировать"
                                    >
                                        <Pencil size={14} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => openMerge(city)}
                                        data-testid={`city-merge-${city.id}`}
                                        className="rounded p-1.5 text-gray-600 transition hover:bg-gray-100"
                                        aria-label="Объединить"
                                    >
                                        <GitMerge size={14} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPendingDelete(city)}
                                        data-testid={`city-delete-${city.id}`}
                                        className="rounded p-1.5 text-red-600 transition hover:bg-red-50"
                                        aria-label="Удалить"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </span>
                            </div>
                        )}
                    />
                </div>
            )}

            <Modal
                isOpen={isModalOpen}
                onClose={() => setModalOpen(false)}
                title={editing ? 'Редактировать город' : 'Новый город'}
                testId="city-modal"
            >
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="city-form">
                    <Input
                        label="Название"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        error={formErrors.name}
                        data-testid="city-form-name"
                        autoFocus
                    />
                    <Input
                        label="Slug"
                        value={form.slug}
                        onChange={(e) => setForm({ ...form, slug: e.target.value })}
                        placeholder="moskva"
                        error={formErrors.slug}
                        data-testid="city-form-slug"
                    />
                    <Input
                        label="ФИАС ID"
                        value={form.fiasId}
                        onChange={(e) => setForm({ ...form, fiasId: e.target.value })}
                        placeholder="0c5b2444-70a0-4932-980c-b4dc0d3f02b5"
                        data-testid="city-form-fias-id"
                    />
                    <Input
                        label="Регион"
                        value={form.regionName}
                        onChange={(e) => setForm({ ...form, regionName: e.target.value })}
                        placeholder="Москва"
                        data-testid="city-form-region-name"
                    />
                    <Input
                        label="Порядок сортировки"
                        type="number"
                        value={form.sortOrder}
                        onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                        data-testid="city-form-sort-order"
                    />
                    <Switch
                        checked={form.isActive}
                        onChange={(next) => setForm({ ...form, isActive: next })}
                        label="Активен"
                        testId="city-form-is-active"
                    />

                    {submitError !== null && submitError !== '' && (
                        <div className="text-sm text-red-600" data-testid="city-form-error">
                            {submitError}
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={() => setModalOpen(false)}
                            className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                        >
                            Отмена
                        </button>
                        <button
                            type="submit"
                            disabled={isCreating || isUpdating}
                            data-testid="city-form-submit"
                            className="rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800 disabled:opacity-50"
                        >
                            {editing ? 'Сохранить' : 'Создать'}
                        </button>
                    </div>
                </form>
            </Modal>

            <Modal
                isOpen={mergeSource !== null}
                onClose={closeMerge}
                title="Объединить город"
                testId="city-merge-modal"
            >
                {mergeSource && (
                    <div className="space-y-4" data-testid="city-merge-content">
                        <p className="text-sm text-gray-700">
                            Город{' '}
                            <span className="font-medium">«{mergeSource.name}»</span> будет
                            слит в выбранный целевой город. Зависимости (точки кафе,
                            мероприятия, выбор города у пользователей) перепривяжутся к
                            целевому, а сам «{mergeSource.name}» деактивируется — его URL
                            станет 301-редиректом на целевой город. Действие необратимо.
                        </p>

                        <Select
                            label="Целевой город"
                            value={mergeTargetId}
                            onChange={(e) => setMergeTargetId(e.target.value)}
                            placeholder="— выберите город —"
                            options={mergeTargets.map((c) => ({
                                value: c.id,
                                label: c.regionName ? `${c.name} (${c.regionName})` : c.name
                            }))}
                            data-testid="city-merge-target"
                        />

                        {mergeTargets.length === 0 && (
                            <p
                                className="text-sm text-gray-500"
                                data-testid="city-merge-no-targets"
                            >
                                Нет доступных активных городов для слияния.
                            </p>
                        )}

                        {mergeTarget && (
                            <div
                                className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
                                data-testid="city-merge-summary"
                            >
                                «{mergeSource.name}» будет слит в «{mergeTarget.name}»,
                                «{mergeSource.name}» деактивируется, его URL станет
                                301-редиректом на «{mergeTarget.name}».
                            </div>
                        )}

                        {mergeError !== null && mergeError !== '' && (
                            <div
                                className="text-sm text-red-600"
                                data-testid="city-merge-error"
                            >
                                {mergeError}
                            </div>
                        )}

                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                type="button"
                                onClick={closeMerge}
                                className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                            >
                                Отмена
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    void confirmMerge();
                                }}
                                disabled={isMerging || mergeTargetId === ''}
                                data-testid="city-merge-submit"
                                className="rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800 disabled:opacity-50"
                            >
                                Объединить
                            </button>
                        </div>
                    </div>
                )}
            </Modal>

            <ConfirmDialog
                isOpen={pendingDelete !== null}
                title="Удалить город"
                message={
                    pendingDelete
                        ? `Удалить город «${pendingDelete.name}»? Действие необратимо.`
                        : ''
                }
                confirmLabel="Удалить"
                danger
                onConfirm={() => {
                    void confirmDelete();
                }}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    );
}
